import React, { useEffect, useState, useRef } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlay } from '@fortawesome/free-solid-svg-icons';
import { logAnalyticsEvent } from '../utils/firebase';
import emailjs from '@emailjs/browser';

const ContactSection = styled.section`
  position: relative;
  min-height: 800px;
  background: url('/images/photo_2024-10-30_17-59-44.jpg') center/cover no-repeat;
  align-content: center;
  background-attachment: fixed;
  opacity: 0;
  transform: translateY(30px);
  transition: all 1s ease;
  
  &.visible {
    opacity: 1;
    transform: translateY(0);
  }
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(to bottom, ${props => props.theme.colors.background}, transparent);
    opacity: 0;
    height: 150px;
    transition: opacity 1s ease;
    z-index: 2;
  }
  

  
  &::after {
    content: '';
    position: absolute;
    bottom: -1px;
    left: 0;
    right: 0;
    height: 150px;
    background: linear-gradient(to top, ${props => props.theme.colors.background}, transparent);
    z-index: 2;
  }
  &.visible::before {
    opacity: 1;
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 5rem 2rem;
  position: relative;
  z-index: 1;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 4rem;
  align-items: center;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 2rem;
  }
`;

const ContentLeft = styled.div`
  color: white;
  opacity: 0;
  transform: translateX(-50px);
  transition: all 0.8s ease;
  transition-delay: 0.3s;
  
  .visible & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Category = styled.h4`
  color: #B5873A;
  font-size: 1rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-transform: uppercase;
`;

const Title = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: 1.5rem;
  line-height: 1.2;
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const Description = styled.p`
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: 2rem;
  opacity: 0.9;
`;

const VideoButton = styled.button`
  opacity: 0;
  transform: scale(0.9);
  transition: all 0.5s ease;
  transition-delay: 0.9s;
  
  .visible & {
    opacity: 1;
    transform: scale(1);
  }

  background: transparent;
  border: none;
  color: white;
  display: flex;
  align-items: center;
  gap: 1rem;
  cursor: pointer;
  padding: 0;
  
  .play-icon {
    width: 48px;
    height: 48px;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.3s ease;
  }
  
  &:hover .play-icon {
    background: white;
    color: ${props => props.theme.colors.primary};
  }
`;

const Form = styled.form`
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  padding: 2rem;
  border-radius: 12px;
  opacity: 0;
  transform: translateX(50px);
  transition: all 0.8s ease;
  transition-delay: 0.1s;
  
  .visible & {
    opacity: 1;
    transform: translateX(0);
  }
`;

const Input = styled.input`
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease;
  
  .visible & {
    opacity: 1;
    transform: translateY(0);
  }
  
  &:nth-child(1) { transition-delay: 0.6s; }
  &:nth-child(2) { transition-delay: 0.7s; }
  &:nth-child(3) { transition-delay: 0.8s; }

  width: 100%;
  padding: 1rem;
  margin-bottom: 1rem;
  border: none;
  border-radius: 6px;
  background: rgba(255, 255, 255, 0.9);
  font-size: 1rem;
  
  &::placeholder {
    color: #666;
  }
  
  &:focus {
    outline: none;
    background: white;
  }
`;

const SubmitButton = styled.button`
  opacity: 0;
  transform: translateY(20px);
  transition: all 0.5s ease;
  transition-delay: 0.9s;
  
  .visible & {
    opacity: 1;
    transform: translateY(0);
  }

  width: 100%;
  padding: 1rem;
  background: #FFA500;
  color: white;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 0.3s ease;
  
  &:hover {
    background: #FF8C00;
  }
`;

function Contact() {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    const submittedForm = e.currentTarget;
    setIsSubmitting(true);

    const formData = new FormData(submittedForm);
    const data = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone')
    };

    logAnalyticsEvent('contact_form_submit_attempt', {
      has_name: !!data.name,
      has_email: !!data.email,
      has_phone: !!data.phone
    });

    try {
      const result = await emailjs.send(
        'service_0epd9u2',
        'template_zub3h4k',
        {
          user_name: data.name,
          user_email: data.email,
          subject: 'Website contact request',
          message: `Phone: ${data.phone}`,
        },
        'FaG4NvlhS0B2UD5JC'
      );
      if (result.status === 200) {
        logAnalyticsEvent('contact_form_submit_success');
        alert('Message sent successfully!');
        submittedForm.reset();
      } else {
        logAnalyticsEvent('contact_form_submit_failure', { error: 'API Error' });
        alert('Failed to send message. Please try again.');
      }
    } catch (error) {
      console.error('Error:', error);
      logAnalyticsEvent('contact_form_submit_failure', { error: 'Network Error' });
      alert('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setIsVisible(entry.isIntersecting);
      },
      { threshold: 0.2 }
    );

    const currentRef = sectionRef.current;
    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef);
      }
    };
  }, []);

  return (
    <ContactSection ref={sectionRef} className={isVisible ? 'visible' : ''}>
      <Container>
        <ContentLeft>
          <Category>CALL TO ACTION</Category>
          <Title>Fundarising for the people and the causes you care about</Title>
          <Description>
            Driven by compassion, powered by community. The Big Heart Association is on a mission to bring sustainable change to those who need it most.
          </Description>
          <VideoButton onClick={() => logAnalyticsEvent('video_play_clicked')}>
            <span className="play-icon">
              <FontAwesomeIcon icon={faPlay} />
            </span>
            Play short video
          </VideoButton>
        </ContentLeft>
        
        <Form onSubmit={handleSubmit}>
          <Input 
            type="text" 
            placeholder="Enter Name*" 
            name="name"
            required
          />
          <Input 
            type="email" 
            placeholder="Enter Email*" 
            name="email"
            required
          />
          <Input 
            type="tel" 
            placeholder="Enter Phone No.*" 
            name="phone"
            required
          />
          <SubmitButton type="submit" disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Involve Now'}
          </SubmitButton>
        </Form>
      </Container>
    </ContactSection>
  );
}

export default Contact;
